import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Brandmark } from '@/components/typography/Brandmark';
import { ScriptLine } from '@/components/typography/ScriptLine';
import { GoldLine } from '@/components/ornaments/GoldLine';
import { Sparkle } from '@/components/ornaments/Sparkle';
import { NAV_DESTINATIONS_SHORT } from './nav.config';
import styles from './SiteFooter.module.css';

/**
 * La firma manuscrita va en TRES lineas, como el poster.
 *
 * En una sola linea medía el **44,7 % del ancho a 1024** (35,8 % a 1280, 31,8 % a
 * 1440) contra el **15,6 %** del poster, y no era solo un defecto de fidelidad: al
 * comerse casi la mitad de la banda dejaba la navegacion sin sitio y la partia en
 * tres filas desiguales. Los cortes son los del poster, no los que salgan del
 * ancho disponible: partirlo con un `max-width` daria «Somos luz. Somos» /
 * «origen. Somos nueva» / «humanidad.», que no es lo que dibuja el mockup.
 *
 * El texto leido en voz alta no cambia: el contenido del parrafo sigue siendo la
 * misma frase.
 */
const CLAIM_LINEAS = ['Somos luz.', 'Somos origen.', 'Somos nueva humanidad.'] as const;

/**
 * Donde cae el ✦ de la fila de navegacion.
 *
 * En el poster el destello parte la fila **por la mitad de su ANCHO**, no por el
 * numero de items. Medido en `DEMO_0_Hub.png` a 1024: con sus cuatro rotulos, los
 * dos de la izquierda ocupan 115 px y los dos de la derecha 125 px, y el destello
 * cae al 48 % — el centro. Con nuestros cinco rotulos cortos, ponerlo tras el
 * segundo da 166 px contra 171 px, o sea el 49 %. `floor(n/2)` acierta en los dos
 * casos, y por eso es esta cuenta y no un indice puesto a ojo.
 */
const INDICE_ESTRELLA = Math.floor(NAV_DESTINATIONS_SHORT.length / 2);

/**
 * Pie de marca: logo, destinos, claim manuscrito y NOTA LEGAL PRUDENTE.
 *
 * La nota legal es obligatoria (ADR-003 §4 y CLAUDE.md §5): experiencia de
 * desarrollo personal, sin promesas de curacion ni sellos medicos. No se quita
 * ni se "suaviza" en ninguna pagina.
 */
export function SiteFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <GoldLine width="full" ornament="star" className={styles.topRule} />

      <div className={styles.inner}>
        <div className={styles.brand}>
          <Brandmark layout="stacked" size="md" />
        </div>

        <nav className={styles.nav} aria-label="Destinos de la gira">
          <ul className={styles.list} role="list">
            {NAV_DESTINATIONS_SHORT.map((item, i) => (
              <Fragment key={item.to}>
                {i === INDICE_ESTRELLA ? (
                  /* Ornamento puro: `aria-hidden` lo saca del arbol, asi que la
                   * lista anuncia solo los destinos, sin la estrella. */
                  <li className={styles.starItem} aria-hidden="true">
                    <Sparkle size={30} className={styles.star} />
                  </li>
                ) : null}
                <li>
                  <Link className={styles.link} to={item.to}>
                    {item.label}
                  </Link>
                </li>
              </Fragment>
            ))}
          </ul>
        </nav>

        <ScriptLine className={styles.claim} size="sm" align="start">
          {CLAIM_LINEAS.map((linea) => (
            <Fragment key={linea}>
              <span className={styles.claimLine}>{linea}</span>
              {/* El espacio es para el TEXTO, no para el dibujo: sin el,
                  `textContent` sale «Somos luz.Somos origen.…» pegado y cualquier
                  nombre accesible que se componga de ahi lo hereda. El navegador
                  descarta el blanco entre dos cajas de bloque, asi que no anade
                  ninguna linea — comprobado contando cajas de linea. */}{' '}
            </Fragment>
          ))}
        </ScriptLine>
      </div>

      <div className={styles.legal}>
        <p className={styles.disclaimer}>
          Los encuentros de la Gira 2027 son una experiencia de desarrollo personal y espiritual. No
          constituyen un tratamiento médico ni psicológico, no sustituyen la atención sanitaria profesional y
          no prometen resultados de salud.
        </p>
        <p className={styles.copyright}>© {year} Escuela Pleyadiana · Azrahel del Mayor</p>
      </div>
    </footer>
  );
}
